import { joiResolver } from '@hookform/resolvers/joi';

import { makeStyles } from 'tss-react/mui';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import {
  ApiButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  stringToPayloadFormat,
  Typography,
  useSnackbarHandler,
} from 'web-library';
import { UnusedExamLevels } from '../../../../api-refactored/exam-configuration/useGetUnusedExamLevels';
import { usePostAdmissionLevelVacancies } from '../../../../api-refactored/exam-configuration/usePostAdmissionLevelVacancies';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import NumberOnlyFormItem from '../../../../components/form-items/NumberOnlyFormItem';
import AddAdmissionLevel, { AddAdmissionLevelData } from './schema/AddAdmissionLevel';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

const useStyles = makeStyles()((theme) =>
({
  customClass: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  controlledAutoComplete: {
    paddingTop: theme.spacing(3),
  },
  selectAdmissionLeveltitle: {
    fontWeight: 800,
    color: theme.color.text.textPrimary,
  },
  yearOfAdmissionTitle: {
    fontWeight: 800,
    color: theme.color.text.textPrimary,
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(3),
  },
  datePickerContainer: {
    marginBottom: theme.spacing(3),
  },
}));

interface AddAdmissionLevelModalProps {
  modalState: boolean;
  onDialogClose: () => void;
  selectedYear: number;
  selectedExamTypeUuid: string;
  admissionLevel: string;
  dateOfBirthFrom: string;
  dateOfBirthTo: string;
  examVacancy: string;
  apiButtonStatus: MutationStatus;
  setAdmissionLevel: React.Dispatch<React.SetStateAction<string>>;
  setDateOfBirthFrom: React.Dispatch<React.SetStateAction<string>>;
  setDateOfBirthTo: React.Dispatch<React.SetStateAction<string>>;
  setExamVacancy: React.Dispatch<React.SetStateAction<string>>;
  setApiButtonStatus: React.Dispatch<React.SetStateAction<MutationStatus>>;
  refetchAdmissionLevelVacancies: () => void;
  unUsedExamLevels: UnusedExamLevels[];
  refetchLastUpdatedInfo: () => void;
}

export interface ExamLevelSeriesObjectType {
  examLevelUuid: string;
  examSeriesUuid: string;
}

const AddAdmissionLevelModal = ({
  modalState,
  onDialogClose,
  selectedYear,
  selectedExamTypeUuid,
  admissionLevel,
  dateOfBirthFrom,
  dateOfBirthTo,
  examVacancy,
  apiButtonStatus,
  setAdmissionLevel,
  setDateOfBirthFrom,
  setDateOfBirthTo,
  setExamVacancy,
  setApiButtonStatus,
  refetchAdmissionLevelVacancies,
  unUsedExamLevels,
  refetchLastUpdatedInfo,
}: AddAdmissionLevelModalProps) => {
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const { classes } = useStyles();

  const [admissionLevelDisplay, setAdmissionLevelDisplay] = useState<string>('');

  const formHook = useForm<AddAdmissionLevelData>({
    mode: 'onSubmit',
    resolver: joiResolver(AddAdmissionLevel()),
  });

  const { control, formState: { errors }, handleSubmit, setValue, reset } = formHook;

  const {
    status: statusAdmissionLevelVacancies,
    mutate: mutatePostAdmissionLevelVacancies,
  } = usePostAdmissionLevelVacancies({
    onSuccess: () => {
      onCloseAction();
      refetchAdmissionLevelVacancies();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>ExamConfiguration.AdmissionLevelAddedToList', {
          admissionLevel: admissionLevelDisplay,
        }),
        targetAnchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
      refetchLastUpdatedInfo();
    },
  });

  const confirmAdmissionLevel = handleSubmit(() => {
    mutatePostAdmissionLevelVacancies({
      admissionLevel,
      dateOfBirthFrom: stringToPayloadFormat(dateOfBirthFrom),
      dateOfBirthTo: stringToPayloadFormat(dateOfBirthTo),
      totalExamVacancy: parseInt(examVacancy, 10),
      year: selectedYear,
      examGroupLevel: selectedExamTypeUuid,
    });
  });

  useEffect(() => setApiButtonStatus(statusAdmissionLevelVacancies), [
    setApiButtonStatus,
    statusAdmissionLevelVacancies,
  ]);

  const onCloseAction = () => {
    onDialogClose();
    reset();
  };

  return (
    <Dialog open={modalState} maxWidth="md">
      <DialogTitle>{t('exam->>ExamConfiguration.AddAdmissionLevel')}</DialogTitle>
      <form onSubmit={confirmAdmissionLevel}>
        <DialogContent>
          <Typography className={classes.selectAdmissionLeveltitle} variant="h3">
            {t('exam->>ExamConfiguration.SelectAdmissionLevel')}
          </Typography>
          <Box className={classes.controlledAutoComplete}>
            {unUsedExamLevels && (
              <ControlledAutocomplete
                label={t('exam->>ExamConfiguration.AdmissionLevel')}
                control={control}
                name="admissionLevel"
                id="input-select-special-consideration-exam-level"
                options={unUsedExamLevels}
                getOptionLabel={(option: string | UnusedExamLevels) => (typeof option === 'string' ? option : option.description)}
                renderOption={(_, option) => option.description}
                placeholder="Select admission level"
                error={!!errors.admissionLevel}
                helperText={errors?.admissionLevel?.message?.toString()}
                transform={{
                  input: (selectedOption, options) =>
                    options.find((option) => option.code === selectedOption) ?? null,
                  output: (selectedOption) => {
                    const selectedAdmissionLevel = selectedOption ? selectedOption.code : null;
                    const selectedAdmissionLevelDisplay = selectedOption
                      ? selectedOption.description
                      : null;
                    setAdmissionLevel(selectedAdmissionLevel);
                    setAdmissionLevelDisplay(selectedAdmissionLevelDisplay);
                    return selectedAdmissionLevel;
                  },
                }}
              />
            )}
          </Box>
          <Typography className={classes.yearOfAdmissionTitle} variant="h3">
            {t('exam->>ExamConfiguration.YearOfAdmissionAndExamVacancy')}
          </Typography>
          <Grid className={classes.datePickerContainer} container spacing={4} direction="row">
            <Grid md={6} sm={12} xs={12}>
              <ControlledDatePicker
                name="dateOfBirthFrom"
                label={t('exam->>ExamConfiguration.DateofBirthFrom')}
                // value={dateOfBirthFrom}
                  control={control}
                onChange={(date) => {
                  setDateOfBirthFrom(date || '');
                  setValue('dateOfBirthFrom', date || '');
                }}
                error={!!errors.dateOfBirthFrom}
                helperText={errors?.dateOfBirthFrom?.message?.toString()}
                disableFuture
                inputVariant="filled"
              />
            </Grid>
            <Grid md={6} sm={12} xs={12}>
              <ControlledDatePicker
                name="dateOfBirthTo"
                label={t('exam->>ExamConfiguration.DateofBirthTo')}
                // value={dateOfBirthTo}
                control={control}
                onChange={(date) => {
                  setDateOfBirthTo(date || '');
                  setValue('dateOfBirthTo', date || '');
                }}
                maxDate={dayjs()}
                error={!!errors.dateOfBirthTo}
                helperText={errors?.dateOfBirthTo?.message?.toString()}
                disableFuture
                inputVariant="filled"
              />
            </Grid>
          </Grid>
          <NumberOnlyFormItem
            id="examVacancy"
            label={t('exam->>ExamConfiguration.ExamVacancy')}
            name="examVacancy"
            type="tel"
            form={formHook}
            error={!!errors.examVacancy}
            helperText={errors?.examVacancy?.message?.toString()}
            fullWidth
            placeholder={t('exam->>ExamConfiguration.EnterExamVacancy')}
            onChange={(e: React.SyntheticEvent<EventTarget>) =>
              setExamVacancy((e.target as HTMLFormElement).value)
            }
          />
        </DialogContent>
        <DialogActions>
          <Box className={classes.buttonContainer}>
            <Button id="button-cancel" onClick={onCloseAction} variant="text">
              {t('exam->>ExamConfiguration.Cancel')}
            </Button>
            <ApiButton id="button-confirm-admission-level" type="submit" status={apiButtonStatus}>
              {t('exam->>ExamConfiguration.ConfirmAdmissionLevel')}
            </ApiButton>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};
export default AddAdmissionLevelModal;
